import { getCleanLanguageCode } from "components/Beneficiar/utils";
import { extractCountryCode, getCurrencySymbol, getJsonOrFalse } from "helpers/utils";
import { useCallback, useEffect } from "react";
import { fetchCompanyInfo, putMe } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TPortalType } from "store/types";
import { EPortalType } from "store/types";
import type { CountryFeatures } from "types/countryFeatures";
import { defaultCountryFeatures } from "types/countryFeatures";

import { reloadAfterCompanySwitch } from "./companySwitchLocation";

/**
 * This object contains booleans to check if we are on some companies
 */
export type IsCompanyWithExceptions = Record<CompaniesWithExceptions, boolean>;

export type CompaniesWithExceptions = "arte";

interface UseCompanySettingsProps {
    portalType?: TPortalType;
}

export type CompanySettingList =
    | "authorized_publisher_seats"
    | "authorized_agreement_templates_limit"
    | "accounting_software"
    | "uses_portal"
    | "uses_stock"
    | "term_statement_email_template"
    | "beneficiary_statement_email_template"
    | "contract_agreement_template"
    | "uses_statement_numbering"
    | "statement_numbering_template"
    | "folio_based_by_default"
    | "net_statement_show_contributor_cost"
    | "available_distributors"
    | "accepted_payment_methods"
    | "uses_statement_social_security_number"
    | "uses_portal_social_profile"
    | "preferred_title_identifier"
    | "invoice_numbering"
    | "uses_multiple_brands"
    | "portal_welcome_email_template"
    | "vat_multiplier"
    | "show_zero_percent_on_statement"
    | "preferred_accounting_group_method"
    | "uses_agreements"
    | "custom_statement_wording"
    | "custom_statement_wording_en"
    | "uses_interchannel_compensation"
    | "uses_intertitle_compensation"
    | "uses_agreement_validation_chain"
    | "uses_interterm_compensation"
    | "uses_simultaneous_period"
    | "uses_multiple_currencies"
    | "active_alternative_currencies"
    | "uses_price_with_vat"
    | "uses_initial_advance"
    | "uses_custom_statement_templates"
    | "always_pay_collective_and_other"
    | "show_sold_quantity_in_consolidated_statement"
    | "show_balance_in_consolidated_statement"
    | "uses_accounting_with_channel_dispatch"
    | "enforce_mfa"
    | "uses_market_segmentation"
    | "uses_metadata"
    | "uses_agreements_only"
    | "press_uses_multiple_channels"
    | "hide_sales_zero_percent_term"
    | "hide_zero_lines_on_statement"
    | "hide_zero_percent_terms_on_statement"
    | "press_preferred_channel"
    | "uses_overriden_price_on_sales"
    | "uses_transfer_term" // Whether the company uses rights transfer (cessions)
    | "analytics_layout"
    | "uses_analytics"
    | "uses_urssaf_declarations"
    | "show_advance_amount_in_letters_on_agreements"
    | "show_initial_advance_amount_in_letters_on_agreements"
    | "show_rates_in_letters_on_agreements"
    | "representative_roles"
    | "agreements_show_signatory_job_title"
    | "agreements_show_signature_date"
    | "uses_ircec"
    | "press_agreements_detach_statements"
    | "show_title_consolidated_sales"
    | "uses_worker_mode"
    | "accounting_custom_extra_columns"
    | "uses_minimum_guaranteed_payment"
    | "uses_beneficiary_profile_complete_check"
    | "uses_accountant_mail_remind"
    | "authorized_publisher_seats_per_role"
    | "analytics_refresh_context"
    | "uses_twenty_percent_vat"
    | "accounting_pnm_linebreak"
    | "uses_scoped_notifications"
    | "beneficiary_merged_statements_enabled"
    | "exclude_zero_royalties_statements_on_merged"
    | "beneficiary_merged_statements_requested"
    | "ftp_username"
    | "show_balance_update_on_statement"
    | "show_provision_lines_on_statement"
    | "show_sku_on_statement"
    | "show_ean13_on_statement"
    | "show_release_date_on_statement"
    | "show_term_channel_percentage_on_statement"
    | "uses_distributor_sales_data"
    | "uses_interforum_edi"
    | "uses_cumulative_royalties"
    | "hide_zero_sales_channels"
    | "allows_subscription_channels"
    | "uses_custom_roles"
    | "show_channel_title_price_on_statement"
    | "show_channel_period_revenue_on_statement"
    | "show_type_1_2_as_compensated_statement"
    | "hidden_stock_lines_on_statement"
    | "hide_gross_rights_ttc_on_consolidated_statement"
    | "show_amount_or_qty_on_statement_annexe"
    | "show_compensation_group_on_statement"
    | "agreements_internal_signer_header_text"
    | "agreements_internal_signer_header_text_enabled"
    | "agreements_internal_signer_header_text_enabled_default"
    | "international_banking_enabled"
    | "intl_payment_batch_booking"
    | "payment_file_consolidation_code"
    | "accounting_establishment_code"
    | "accounting_analytic_service"
    | "accounting_analytic_operation"
    | "display_agreement_public_id"
    | "uses_beneficiary_documents_tracking"
    | "beneficiary_documents_reminder_email_template_fr"
    | "beneficiary_documents_reminder_email_template_en"
    | "docoon_sign_sequential_default";

export const OPENING_BALANCE_LETTERS_SETTING = "show_advance_amount_in_letters_on_agreements";
export const INITIAL_ADVANCE_LETTERS_SETTING = "show_initial_advance_amount_in_letters_on_agreements";

/** Initial advance follows the opening-balance setting until the company saves its own value. */
export const resolveAdvanceAmountLetters = (getCompanySettings: (name: CompanySettingList) => unknown) => {
    const openingBalanceInLetters = !!getCompanySettings(OPENING_BALANCE_LETTERS_SETTING);
    const storedInitialAdvanceInLetters = getCompanySettings(INITIAL_ADVANCE_LETTERS_SETTING);
    return {
        openingBalanceInLetters,
        initialAdvanceInLetters:
            storedInitialAdvanceInLetters == null ? openingBalanceInLetters : !!storedInitialAdvanceInLetters,
        initialAdvanceLettersStored: storedInitialAdvanceInLetters != null,
    };
};

const excludeBooleanConvert: CompanySettingList[] = [
    "authorized_publisher_seats",
    "authorized_agreement_templates_limit",
    "agreements_internal_signer_header_text",
    "accounting_establishment_code",
    "accounting_analytic_service",
    "accounting_analytic_operation",
];

/**
 * Hook for getting company infos (name, siret, etc) and settings (uses_portal, uses_stock, etc)
 * @example ```ts
 * const { companyInfos, getCompanySettings } = useCompanySettings()
 * const usesMultipleBrand = getCompanySettings("uses_multiple_brands")
 * ```
 * @returns Company infos and getCompanySettings
 */
export const useCompanySettings = (props?: UseCompanySettingsProps) => {
    const {
        companySettings: companyInfos,
        features,
        i18nKeys,
        currentPortal,
        companyInfoStatus,
    } = useAppSelector((state) => state.settings);
    const portalTypeFromBackend = companyInfos?.publisherType;
    const dispatch = useAppDispatch();

    /**
     * Get a specific company setting (returns boolean for those with '0' or '1' as content)
     * @param name Name of the setting to get
     * @returns The setting content or null if not found
     */
    const getCompanySettings = useCallback(
        (name: CompanySettingList) => {
            if (!companyInfos) return null;
            const setting = companyInfos.settings.find((setting) => setting.name == name);
            if (!setting) return null;
            const content = setting.content;
            if (excludeBooleanConvert.includes(name)) return content;
            if (!content) return false;
            if (content == "0" || content == "1") return !!+content;
            if (["analytics_layout", "analytics_refresh_context", "hidden_stock_lines_on_statement"].includes(name))
                return getJsonOrFalse(content);
            return content;
        },
        [companyInfos],
    );

    /**
     * Switch to another company
     * Will check if the id isn't the same as the current one
     * Then it will set companyId in localStorage to fire a Storage event so every other crealo tab updates (event code is in App.tsx)
     * And reload if shouldReload is true
     * @param companyId Company to switch to
     * @param shouldReload Boolean if page should reload after switching
     */
    const handleSwitchCompany = async (companyId: number, shouldReload?: boolean) => {
        if (companyId === companyInfos?.id) return;
        await dispatch(putMe({ companyId }));
        localStorage.setItem("companyId", companyId.toString());
        sessionStorage.setItem("payment_page_data", "{}");
        if (shouldReload) reloadAfterCompanySwitch();
    };

    useEffect(() => {
        if (!companyInfos && currentPortal && currentPortal != EPortalType.BENEFICIARY && companyInfoStatus === "idle")
            dispatch(fetchCompanyInfo());
    }, [companyInfos, currentPortal]);

    /**
     * Companies with exceptions
     * Unconventional temporary method - Because we have special conditions for companies
     */
    const isCompany: IsCompanyWithExceptions = {
        arte: [205, 206, 207].includes(companyInfos?.id ?? -1),
    };

    const companyLanguage = () => {
        let code: string | undefined = undefined;
        if (companyInfos?.countryCode.includes("-")) {
            code = companyInfos.countryCode.split("-")[0];
        } else {
            code = companyInfos?.countryCode;
        }
        return getCleanLanguageCode(code);
    };

    /** Country code extracted from companyInfos.countryCode for VAT purposes */
    const companyCountryCode = extractCountryCode(companyInfos?.countryCode ?? "") || "FR";

    const companyCurrency = companyInfos?.currency || "EUR";
    const currencySymbol = getCurrencySymbol(companyCurrency);

    /**
     * Typed country features from the backend (ICountryFeatures).
     * Falls back to defaultCountryFeatures (France) while loading.
     * Merges with defaults to guard against partial responses (missing new fields).
     * Use this instead of hardcoded `isBelgium` / `isCanada` checks.
     */
    const countryFeatures: CountryFeatures = features
        ? {
              ...defaultCountryFeatures,
              ...features,
              statementBlocks: features.statementBlocks ?? defaultCountryFeatures.statementBlocks,
              validation: features.validation ?? defaultCountryFeatures.validation,
          }
        : defaultCountryFeatures;

    const internationalBankingEnabled = !!getCompanySettings("international_banking_enabled");

    // Cegid GECT entries carry the term reference as dossier: always grouped by term
    const usesCegidGect = getCompanySettings("accounting_software") === "cegid_gect";

    const localBankingModeLabel =
        countryFeatures.localBankingModeLabel ??
        (countryFeatures.bankingFields.includes("iban")
            ? "PaymentMode.LocalMode.SEPA"
            : "PaymentMode.LocalMode.Generic");

    return {
        companyInfos,
        getCompanySettings,
        handleSwitchCompany,
        portalType: portalTypeFromBackend,
        features,
        countryFeatures,
        i18nKeys,
        isCompany,
        internationalBankingEnabled,
        usesCegidGect,
        localBankingModeLabel,
        companyLanguage: companyLanguage(),
        companyCountryCode,
        companyCurrency,
        currencySymbol,
    };
};
